/**
 * Pure helpers for the Replay tab.
 *
 * Two responsibilities:
 *   1. Build a replay timeline from an EventLog export. We accept either the
 *      EventLogTab download shape (`{ raw: UiEventLogEntry[] }`) or a bare
 *      array of those entries — both shapes round-trip through this loader.
 *   2. Walk a frame's payload and rewrite any `transactionId` field by an
 *      oldToNew map. Used after a replayed StartTransaction: the live CSMS
 *      assigns a new tx id, and every subsequent frame that referenced the
 *      historical tx id has to be updated in lockstep.
 *
 * Both functions are pure so the unit suite can pin their behaviour without
 * spinning up a store or a mock CSMS.
 */

export interface ReplayStep {
  readonly seq: number;
  readonly action: string;
  /**
   * Live payload sent during Play. Mutable via
   * useReplayStore.setStepPayload — FEAT-025 lets QA tweak captured
   * payloads (e.g. swap idTag, change connectorId) without reloading
   * the source file.
   */
  payload: Record<string, unknown>;
  /**
   * FEAT-025 — frozen copy of the initial payload at build time.
   * `setStepPayload` never touches this; `revertStepPayload` restores
   * `payload` from it, so users can always get back to the captured
   * frame.
   */
  readonly originalPayload: Record<string, unknown>;
  readonly originalCreatedAt: number;
  readonly originalMessageId: string;
  /** User-controlled: rows marked skipped are not sent during Play. */
  skipped: boolean;
  status: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  /** Captured CALLERROR / OcppCallError message when `status === 'failed'`. */
  error: string | null;
}

/**
 * FEAT-025 — deep clone a JSON-shaped object so `payload` and
 * `originalPayload` never share references. Payloads are always
 * JSON-serializable OCPP frames, so JSON-round-trip is safe and
 * faster than `structuredClone` here.
 */
export function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

interface RawEntry {
  readonly seq?: number;
  readonly direction?: 'in' | 'out';
  readonly messageType?: 'CALL' | 'CALLRESULT' | 'CALLERROR';
  readonly messageId?: string;
  readonly action?: string | null;
  readonly payload?: unknown;
  readonly at?: number;
  readonly createdAt?: number;
}

/**
 * FEAT-008 — supported source formats. evse3's own EventLog export
 * uses `{ raw: UiEventLogEntry[] }` (or a bare entries array); real
 * CPMS server logs use `{ timestamp, payload: '[2,"id","Action",...]' }`
 * where the payload is the OCPP frame as a JSON string. Auto-detect
 * by first-entry shape so users don't have to pick.
 */
export type ReplayFormat = 'evse3' | 'cpms' | 'unknown';

export function detectFormat(raw: unknown): ReplayFormat {
  let arr: readonly unknown[] | undefined;
  if (Array.isArray(raw)) {
    arr = raw as readonly unknown[];
  } else if (raw && typeof raw === 'object') {
    const w = raw as { raw?: unknown; entries?: unknown };
    if (Array.isArray(w.raw)) arr = w.raw as readonly unknown[];
    else if (Array.isArray(w.entries)) arr = w.entries as readonly unknown[];
  }
  if (!arr || arr.length === 0) return 'unknown';
  const first = arr[0];
  if (!first || typeof first !== 'object') return 'unknown';
  const o = first as Record<string, unknown>;
  // CPMS shape: payload is a string AND timestamp is an ISO-ish string.
  if (typeof o['payload'] === 'string' && typeof o['timestamp'] === 'string') {
    return 'cpms';
  }
  // evse3 shape: payload is an object + direction + messageType present.
  if (
    typeof o['direction'] === 'string' &&
    typeof o['messageType'] === 'string'
  ) {
    return 'evse3';
  }
  return 'unknown';
}

/**
 * Accept either the EventLogTab download wrapper, a bare entries array,
 * or a CPMS server log (auto-detected). Returns a unified `ReplayStep[]`.
 */
export function buildTimeline(raw: unknown): ReplayStep[] {
  const format = detectFormat(raw);
  if (format === 'cpms') return buildTimelineFromCpms(raw);
  return buildTimelineFromEvse3(raw);
}

export function buildTimelineFromEvse3(raw: unknown): ReplayStep[] {
  const entries = extractEntries(raw);
  const steps: ReplayStep[] = [];
  for (const e of entries) {
    if (e.direction !== 'out') continue;
    if (e.messageType !== 'CALL') continue;
    if (typeof e.action !== 'string' || e.action.length === 0) continue;
    if (typeof e.payload !== 'object' || e.payload === null) continue;
    const captured = cloneJson(e.payload as Record<string, unknown>);
    steps.push({
      seq: typeof e.seq === 'number' ? e.seq : steps.length + 1,
      action: e.action,
      payload: cloneJson(captured),
      originalPayload: captured, // FEAT-025 — frozen snapshot for Revert
      originalCreatedAt: e.at ?? e.createdAt ?? 0,
      originalMessageId: e.messageId ?? '',
      skipped: false,
      status: 'pending',
      error: null,
    });
  }
  steps.sort((a, b) => a.originalCreatedAt - b.originalCreatedAt || a.seq - b.seq);
  return steps.map((s, i) => ({ ...s, seq: i + 1 }));
}

/**
 * OCPP 1.6J actions a CSMS sends TO the CP — these must be filtered
 * out when replaying a CPMS log "outward", because they were INBOUND
 * at the original CP and replaying them as outbound would be incoherent.
 * Anything else is presumed CP→CSMS (the side we're simulating).
 */
const INBOUND_ACTIONS = new Set<string>([
  'Reset',
  'RemoteStartTransaction',
  'RemoteStopTransaction',
  'ChangeAvailability',
  'ChangeConfiguration',
  'GetConfiguration',
  'GetCompositeSchedule',
  'SetChargingProfile',
  'ClearChargingProfile',
  'UnlockConnector',
  'TriggerMessage',
  'SendLocalList',
  'GetLocalListVersion',
  'CancelReservation',
  'ReserveNow',
  'UpdateFirmware',
  'GetDiagnostics',
]);

interface CpmsEntry {
  readonly timestamp?: string;
  readonly payload?: string;
}

export function buildTimelineFromCpms(raw: unknown): ReplayStep[] {
  const arr =
    Array.isArray(raw)
      ? (raw as readonly CpmsEntry[])
      : ([] as readonly CpmsEntry[]);
  const steps: ReplayStep[] = [];
  for (const e of arr) {
    if (typeof e.payload !== 'string') continue;
    let frame: unknown;
    try {
      frame = JSON.parse(e.payload);
    } catch {
      continue;
    }
    if (!Array.isArray(frame) || frame.length < 2) continue;
    const [type, messageId, action, body] = frame as [
      unknown,
      unknown,
      unknown,
      unknown,
    ];
    if (type !== 2) continue; // only CALLs
    if (typeof action !== 'string' || action.length === 0) continue;
    if (INBOUND_ACTIONS.has(action)) continue; // CSMS → CP, skip
    if (body && typeof body !== 'object') continue;
    const at = typeof e.timestamp === 'string' ? Date.parse(e.timestamp) : 0;
    const captured = cloneJson((body ?? {}) as Record<string, unknown>);
    steps.push({
      seq: steps.length + 1,
      action,
      payload: cloneJson(captured),
      originalPayload: captured, // FEAT-025 — frozen snapshot for Revert
      originalCreatedAt: Number.isFinite(at) ? at : 0,
      originalMessageId: typeof messageId === 'string' ? messageId : '',
      skipped: false,
      status: 'pending',
      error: null,
    });
  }
  steps.sort((a, b) => a.originalCreatedAt - b.originalCreatedAt || a.seq - b.seq);
  return steps.map((s, i) => ({ ...s, seq: i + 1 }));
}

function extractEntries(raw: unknown): readonly RawEntry[] {
  if (Array.isArray(raw)) return raw as RawEntry[];
  if (raw && typeof raw === 'object') {
    const wrapper = raw as { raw?: unknown; entries?: unknown };
    if (Array.isArray(wrapper.raw)) return wrapper.raw as RawEntry[];
    if (Array.isArray(wrapper.entries)) return wrapper.entries as RawEntry[];
  }
  return [];
}

/**
 * Rewrites every `transactionId` field in `payload` whose value is a key in
 * `oldToNew`. Walks objects + arrays recursively. Returns a fresh object —
 * the input is never mutated, so a caller can safely keep the original
 * frame for the "original" column in the UI.
 *
 * Only the field name `transactionId` is rewritten — every OCPP 1.6 action
 * that carries a tx id uses exactly that field name, so a name-driven walk
 * is sufficient without per-action knowledge.
 */
export function applyTxIdRemap(
  payload: Record<string, unknown>,
  oldToNew: ReadonlyMap<number, number>,
): Record<string, unknown> {
  if (oldToNew.size === 0) return payload;
  return walk(payload, oldToNew) as Record<string, unknown>;
}

function walk(value: unknown, oldToNew: ReadonlyMap<number, number>): unknown {
  if (Array.isArray(value)) {
    return value.map((v) => walk(v, oldToNew));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === 'transactionId' && typeof v === 'number' && oldToNew.has(v)) {
        out[k] = oldToNew.get(v);
        continue;
      }
      out[k] = walk(v, oldToNew);
    }
    return out;
  }
  return value;
}

/** Count of pending (not skipped, not done) steps. Cheap selector for the UI. */
export function countPending(steps: readonly ReplayStep[]): number {
  return steps.filter((s) => !s.skipped && s.status === 'pending').length;
}
